import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { RiImageAddLine } from "react-icons/ri";
import Pagination from "../../../../../libs/Pagination";
import { addHomeSection, homeSectionSequence, deleteHomeSection, getHomeSections, updateHomeSection, swapHomeSectionWithPage } from "../../../../../libs/api/homeSectionApi";
import { AppContext } from "../../../../../context/appContext";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import Swal from "sweetalert2";
import InternalLinkBuilder from "../../../../../libs/components/InternalLinkBuilder";


// Helper function to extract YouTube video ID from various URL formats
const extractYouTubeVideoId = (input) => {
  if (!input) return null;

  // Remove whitespace
  input = input.trim();

  // If it's already just an ID (11 characters, alphanumeric with dash/underscore)
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }

  // Pattern 1: youtube.com/watch?v=VIDEO_ID
  let match = input.match(/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 2: youtu.be/VIDEO_ID
  match = input.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 3: youtube.com/embed/VIDEO_ID
  match = input.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  // Pattern 4: m.youtube.com/watch?v=VIDEO_ID
  match = input.match(/(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (match) return match[1];

  return null; // Invalid format
};

// Helper function to fetch video title from YouTube oEmbed API
const fetchYouTubeTitle = async (videoId) => {
  if (!videoId) return null;

  try {
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );

    if (!response.ok) {
      return null; // Video not found or private
    }

    const data = await response.json();
    return data.title || null;
  } catch (error) {
    console.error('Error fetching YouTube title:', error);
    return null;
  }
};

export default function HomeSectionContent({ currentCategoryId = null }) {
  const { homeSectionCategoriesDropdown, fetchHomeSectionCategoriesDropdown, t, formatNum, isRTL } = useContext(AppContext);
  const [contents, setContents] = useState([]);
  const [category, setCategory] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [contentImage, setContentImage] = useState(null);
  const [youtubeVideoIdInput, setYoutubeVideoIdInput] = useState(""); // User's input (URL or ID)
  const [extractedVideoId, setExtractedVideoId] = useState(""); // Extracted video ID
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [errors, setErrors] = useState({ title: "", image: "", youtubeVideoId: "", category: "" });
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loadingAddForm, setLoadingAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Entry type states
  const [entryType, setEntryType] = useState("manual"); // "manual" or "youtube"

  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    fetchContents(true);
  }, [currentPage, limit, debouncedSearch, currentCategoryId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const fetchContents = async (isLoading) => {
    setLoading(isLoading);
    try {
      const categoryIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getHomeSections(currentPage, limit, debouncedSearch, categoryIdFilter);
      if (response.status === 200) {
        // Add sequence number to each content for drag and drop
        const contentsWithSequence = response.data.data.map((content, index) => ({
          ...content,
          sequence: content.sequence || index + 1
        }));
        setContents(contentsWithSequence || []);
        setTotalPages(response.data.totalPages || 1);
        setLimit(response.data.limit || 10);
        setCurrentPage(response.data.currentPage || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'title':
        if (!value || !value.toString().trim()) {
          error = t('validation.titleRequired');
        }
        break;
      case 'image':
        if (entryType === "manual" && modalMode === "add" && !value) {
          error = t('validation.imageRequired');
        } else if (value && value.size && value.size > 25 * 1024 * 1024) {
          error = t('validation.imageSizeLimit');
        }
        break;
      case 'youtubeVideoId':
        if (entryType === "youtube") {
          if (!value || !value.toString().trim()) {
            error = t('validation.youtubeVideoIdRequired');
          } else {
            // Try to extract video ID
            const extracted = extractYouTubeVideoId(value);
            if (!extracted) {
              error = t('validation.invalidYoutubeUrl');
            }
          }
        }
        break;
      case 'category':
        // Category is now optional, no error needed
        break;
      default:
        break;
    }
    return error;
  };

  const handleFieldChange = async (name, value) => {
    if (name === 'title') setTitle(value);
    if (name === 'description') setDescription(value);
    if (name === 'link') setLink(value);
    if (name === 'image') setContentImage(value);
    if (name === 'youtubeVideoId') {
      setYoutubeVideoIdInput(value);
      // Extract video ID in real-time
      const extracted = extractYouTubeVideoId(value);
      setExtractedVideoId(extracted || '');

      // Auto-fetch title if we have a valid video ID and title is empty
      if (extracted && !title) {
        setIsFetchingTitle(true);
        setTitle(t('common.loading'));
        const fetchedTitle = await fetchYouTubeTitle(extracted);
        if (fetchedTitle) {
          setTitle(fetchedTitle);
        } else {
          setTitle(''); // Reset if fetch failed
        }
        setIsFetchingTitle(false);
      }
    }
    if (name === 'category') setCategory(value);
    // Validate field
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleLinkChange = (url, metadata) => {
    setLink(url);
  };

  const handleSubmit = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);

    if (entryType === "manual") {
      newErrors.image = validateField('image', contentImage);
    } else {
      newErrors.youtubeVideoId = validateField('youtubeVideoId', youtubeVideoIdInput);
    }

    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    let payload;

    if (entryType === "manual") {
      // Manual entry with image and link
      const formData = new FormData();
      formData.append('title', title);
      if (description) formData.append('description', description);
      if (link) formData.append('url', link);
      if (contentImage) formData.append('image', contentImage);
      if (category) formData.append('categoryId', category);
      formData.append('isHidden', isHidden ? 'true' : 'false');
      payload = formData;
    } else {
      // YouTube entry
      payload = {
        title,
        youtubeVideoId: extractedVideoId,
        isHidden: isHidden,
      };
      if (description) payload.description = description;
      if (category) payload.categoryId = category;
    }

    try {
      setLoadingAddForm(true);
      const response = await addHomeSection(payload);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("home_section_modal").close();
        toast.success(response?.data?.message || t('messages.contentAddedSuccess'));
        fetchContents(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};
    newErrors.title = validateField('title', title);

    if (entryType === "manual") {
      // Image is optional in edit mode for manual entry
    } else {
      newErrors.youtubeVideoId = validateField('youtubeVideoId', youtubeVideoIdInput);
    }

    setErrors(newErrors);

    // If there are errors, do not proceed
    if (Object.keys(newErrors).some((key) => newErrors[key])) return;

    let payload;

    // On update every editable field is sent, even when empty, so clearing a
    // description / link / category actually reaches the backend.
    if (entryType === "manual") {
      // Manual entry with image and link
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description || '');
      formData.append('url', link || '');
      if (contentImage && typeof contentImage !== 'string') {
        formData.append('image', contentImage);
      }
      formData.append('categoryId', category || '');
      formData.append('youtubeVideoId', '');
      formData.append('isHidden', isHidden ? 'true' : 'false');
      payload = formData;
    } else {
      // YouTube entry
      payload = {
        title,
        description: description || '',
        youtubeVideoId: extractedVideoId,
        categoryId: category || null,
        isHidden: isHidden,
      };
    }

    try {
      setLoadingAddForm(true);
      const response = await updateHomeSection(selectedContentId, payload);
      if (response.status === 200 || response.status === 201) {
        document.getElementById("home_section_modal").close();
        toast.success(response?.data?.message || t('messages.contentUpdatedSuccess'));
        fetchContents(false);
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  }

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert
    const result = await Swal.fire({
      title: t('dialogs.areYouSure'),
      text: t('dialogs.cantRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('dialogs.yesDelete'),
      cancelButtonText: t('dialogs.cancel')
    });

    if (!result.isConfirmed) {
      return; // User cancelled the deletion
    }

    try {
      const response = await deleteHomeSection(id);
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.data?.message || t('messages.contentDeletedSuccess'));
        fetchContents(false);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (mode, content = null) => {
    // Refresh categories dropdown to get latest categories
    if (fetchHomeSectionCategoriesDropdown) {
      fetchHomeSectionCategoriesDropdown();
    }

    setModalMode(mode);
    setSelectedContentId(null);
    if (mode === "edit" && content) {
      setTitle(content?.title || "");
      setDescription(content?.description || "");
      setLink(content?.url || "");
      setContentImage(content?.image || null);
      setYoutubeVideoIdInput(content?.youtubeVideoId || "");
      setExtractedVideoId(content?.youtubeVideoId || "");
      setSelectedContentId(content?._id || null);
      setCategory(content?.categoryId?._id || "");
      setEntryType(content?.youtubeVideoId ? "youtube" : "manual");
      setIsHidden(content?.isHidden || false);
      setErrors({ title: "", image: "", youtubeVideoId: "", category: "" });
    } else {
      setTitle("");
      setDescription("");
      setLink("");
      setContentImage(null);
      setYoutubeVideoIdInput("");
      setExtractedVideoId("");
      setSelectedContentId(null);
      setCategory("");
      setEntryType("manual");
      setIsHidden(false);
      setErrors({ title: "", image: "", youtubeVideoId: "", category: "" });
    }

    document.getElementById("home_section_modal").showModal();
  };

  const clearForm = () => {
    setTitle("");
    setDescription("");
    setLink("");
    setContentImage(null);
    setYoutubeVideoIdInput("");
    setExtractedVideoId("");
    setSelectedContentId(null);
    setCategory("");
    setEntryType("manual");
    setIsHidden(false);
    setErrors({ title: "", image: "", youtubeVideoId: "", category: "" });
  }

  const handleDragStart = (e, index) => {
    setDraggedItem(contents[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    // Create a custom drag image that only shows the dragged item
    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    // Style the drag image to look like a single item
    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    // Hide the original element during drag
    draggedElement.style.opacity = '0.3';

    // Set the custom drag image
    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(contents[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = contents[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedContents = contents.map(content => {
      if (content._id === draggedItem._id) {
        return { ...content, sequence: targetSequence };
      }
      if (content._id === draggedOverItem._id) {
        return { ...content, sequence: draggedSequence };
      }
      return content;
    });

    // Sort by sequence to reflect the swap
    updatedContents.sort((a, b) => a.sequence - b.sequence);

    setContents(updatedContents);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);

    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedUpdateSequence'));
      // Revert if update fails
      fetchContents(false);
    }
  };

  const handleDragEnd = (e) => {
    // Restore the original element's opacity
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await homeSectionSequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchContents(false);
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  }

  const handleSwapWithPage = async (contentId, direction) => {
    try {
      const payload = {
        id: contentId,
        direction: direction,
        currentPage: currentPage,
        limit: limit
      };
      const response = await swapHomeSectionWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.contentMovedSuccessfully'));
        fetchContents(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('content.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Content */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2  border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('content.addContent')}
          </button>
        </div>

        {/* Table */}
        {loading && <div className="flex justify-center items-center h-full p-6"><LoadingSpinner /></div>}
        {!loading && contents.length === 0 && (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('content.noContent')}</p>
          </div>
        )}
        {!loading && contents.length > 0 && (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-7 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('common.id')}</div>
              <div className="p-2 py-4">{t('content.thumbnail')}</div>
              <div className="p-2 py-4">{t('content.contentTitle')}</div>
              <div className="p-2 py-4">{t('content.type')}</div>
              <div className="p-2 py-4">{t('content.link')}</div>
              <div className="p-2 py-4">{t('forms.category')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>

            {/* Draggable Content */}
            <div>
              {contents.map((content, index) => (
                <div
                  key={content._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-7 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === content._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${
                    dragOverItem?._id === content._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                  }`}
                  style={{
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    userSelect: 'none'
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {formatNum((currentPage - 1) * limit + index + 1)}
                  </div>
                  <div className="p-2 py-6">
                    {content?.youtubeVideoId ? (
                      <img
                        src={getYouTubeThumbnail(content.youtubeVideoId)}
                        alt={content.title}
                        className="w-20 h-12 object-cover rounded"
                      />
                    ) : content?.image ? (
                      <img
                        src={content.image}
                        alt={content.title}
                        className="w-20 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400">
                        N/A
                      </div>
                    )}
                  </div>
                  <div className="p-2 py-6 flex items-center gap-2 flex-wrap">
                    <span>{content?.title || "-"}</span>
                    {content?.isHidden && (
                      <span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>
                    )}
                  </div>
                  <div className="p-2 py-6">
                    {content?.youtubeVideoId ? (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">YouTube</span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Manual</span>
                    )}
                  </div>
                  <div className="p-2 py-6">
                    {content?.youtubeVideoId ? (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono">
                        {content.youtubeVideoId}
                      </span>
                    ) : content?.url ? (
                      <a
                        href={content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {content.url.length > 30 ? content.url.substring(0, 30) + '...' : content.url}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="p-2 py-6">{content?.categoryId?.title || "-"}</div>
                  <div className="p-2 py-6">
                    <div className="flex gap-3 items-center">
                      {index === 0 && currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(content._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {index === limit - 1 && currentPage < totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(content._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", content)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(content._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && contents.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')} {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')} {formatNum(totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog
        id="home_section_modal"
        className="modal shadow-[0px_1px_2px_0px_#0A0D120F]"
      >
        {" "}
        <div className="modal-box min-w-[800px] rounded-2xl p-0 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('content.addContent') : t('content.editContent')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Entry Type Tabs - Only show for "add" mode */}
            {modalMode === "add" && (
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  type="button"
                  className={`px-6 py-3 font-medium ${
                    entryType === "manual"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setEntryType("manual")}
                >
                  {t('content.manualEntry') || 'Manual Entry'}
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 font-medium ${
                    entryType === "youtube"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setEntryType("youtube")}
                >
                  {t('content.youtubeVideo') || 'YouTube Video'}
                </button>
              </div>
            )}

            {/* Manual Entry Tab Content */}
            {entryType === "manual" && (
              <>
                {/* Title */}
                <label className="block text-sm font-medium mb-2">{t('content.contentTitle')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder={t('content.enterContentTitle')}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {errors.title && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.title}</p>}

                {/* Description */}
                <label className="block text-sm font-medium mb-2 mt-4">{t('content.descriptionOptional')}</label>
                <textarea
                  value={description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder={t('content.enterContentDescription')}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  rows="4"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />

                {/* Link */}
                <label className="block text-sm font-medium mb-2 mt-4">{t('content.linkOptional')}</label>
                <InternalLinkBuilder
                  value={link}
                  onChange={handleLinkChange}
                  name="url"
                  error=""
                />

                {/* Category */}
                <label className="block text-sm font-medium mb-2 mt-4">
                  {t('content.categoryOptional')}
                </label>
                <select
                  value={category}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <option value="">{t('content.selectCategoryOptional')}</option>
                  {homeSectionCategoriesDropdown?.data?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.displayTitle || cat.title}
                    </option>
                  ))}
                </select>

                {/* Content Image */}
                <div className="my-5 mt-1">
                  <label className="block text-sm font-medium mb-2">
                    {t('content.contentImage')} {modalMode === "add" && `(${t('content.required')})`}
                  </label>
                  <div
                    className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file && file.type.startsWith("image/")) {
                        if (file.size <= 25 * 1024 * 1024) {
                          setContentImage(file);
                          handleFieldChange('image', file);
                          setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                        } else {
                          setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeLimit') }));
                        }
                      }
                    }}
                    onClick={() =>
                      document.getElementById("contentImageUploadInput").click()
                    }
                  >
                    {contentImage ? (
                      <img
                        src={typeof contentImage === 'string' ? contentImage : URL.createObjectURL(contentImage)}
                        alt="content"
                        className="max-h-40 mx-auto mb-2"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                        <div className="bg-gray-100 rounded-full min-h-12 min-w-12 flex items-center justify-center">
                          <RiImageAddLine size={25} className="text-blue-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-md text-blue-500 font-medium">
                            {t('forms.clickToUpload')}
                          </p>
                          <p className="text-md text-black">{t('forms.orDragDrop')}</p>
                        </div>
                        <p className="text-sm text-black">
                          {t('forms.maxFileSize25MB')}
                        </p>
                      </div>
                    )}
                    <input
                      id="contentImageUploadInput"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size <= 25 * 1024 * 1024) {
                            setContentImage(file);
                            handleFieldChange('image', file);
                            setErrors((prevErrors) => ({ ...prevErrors, image: '' }));
                          } else {
                            setErrors((prevErrors) => ({ ...prevErrors, image: t('validation.imageSizeLimit') }));
                          }
                        }
                        e.target.value = '';
                      }}
                    />
                  </div>
                  {errors.image && <p className="text-red-500 absolute mt-1 text-xs">{errors.image}</p>}
                </div>
              </>
            )}

            {/* YouTube Entry Tab Content */}
            {entryType === "youtube" && (
              <>
                {/* Title */}
                <label className="block text-sm font-medium mb-2">{t('content.contentTitle')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder={t('content.enterContentTitle')}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {errors.title && <p className="text-red-500 absolute -mt-[15px] text-xs">{errors.title}</p>}

                {/* YouTube Video ID or URL */}
                <label className="block text-sm font-medium mb-2 mt-4">{t('content.youtubeVideoIdOrUrl')}</label>
                <input
                  type="text"
                  value={youtubeVideoIdInput}
                  onChange={(e) => handleFieldChange('youtubeVideoId', e.target.value)}
                  placeholder={t('content.youtubeUrlPlaceholder')}
                  className="w-full mb-2 border rounded-xl p-3 border-[#808080]"
                  dir="ltr"
                />
                {errors.youtubeVideoId && <p className="text-red-500 text-xs mb-2">{errors.youtubeVideoId}</p>}
                {extractedVideoId && !errors.youtubeVideoId && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className={`text-xs text-green-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('content.videoIdExtracted')}: <span className="font-mono font-semibold">{extractedVideoId}</span>
                    </p>
                  </div>
                )}
                <p className={`text-xs text-gray-500 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('content.youtubeUrlHint')}
                </p>

                {/* Description */}
                <label className="block text-sm font-medium mb-2 mt-4">{t('content.descriptionOptional')}</label>
                <textarea
                  value={description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder={t('content.enterContentDescription')}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  rows="4"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />

                {/* Category */}
                <label className="block text-sm font-medium mb-2 mt-4">
                  {t('content.categoryOptional')}
                </label>
                <select
                  value={category}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <option value="">{t('content.selectCategoryOptional')}</option>
                  {homeSectionCategoriesDropdown?.data?.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.displayTitle || cat.title}
                    </option>
                  ))}
                </select>

                {/* YouTube Preview */}
                {extractedVideoId && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-2">{t('content.preview')}</label>
                    <div className="border rounded-xl overflow-hidden">
                      <img
                        src={getYouTubeThumbnail(extractedVideoId)}
                        alt="YouTube Thumbnail"
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Hidden */}
            <div className="flex items-center gap-2 mt-8">
              <input
                type="checkbox"
                id="homeSectionContent_isHidden"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <label htmlFor="homeSectionContent_isHidden" className="text-sm font-medium cursor-pointer">
                {t('forms.hidden')}
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={loadingAddForm || loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loadingAddForm ? <LoadingSpinner /> : modalMode === "add" ? t('common.add') : t('common.update')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
